import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Spinner } from "react-bootstrap";

import { AuthContext } from "../contexts/AuthContext";
import { MaintenanceProvider } from "../contexts/MaintenanceContext";
import Sidebar from "../components/common/Sidebar";
import AlarmListener from "../components/common/AlarmListener";
import TopNavbar from "../components/common/TopNavbar";

// Tabs
import OverviewTab from "../components/maintenance/tabs/OverviewTab";
import MachinesTab from "../components/maintenance/tabs/MachinesTab";
import ActiveTab from "../components/maintenance/tabs/ActiveTab";
import HistoryTab from "../components/maintenance/tabs/HistoryTab";
import SectorsTab from "../components/maintenance/tabs/SectorsTab";
import MTTRTab from "../components/maintenance/tabs/MTTRtab";
import KaizenTab from "../components/maintenance/tabs/KaizenTab";

import StockShortTab from "../components/maintenance/tabs/StockShortTab";
import StockLongTab from "../components/maintenance/tabs/StockLongTab";
import ToolsTab from "../components/maintenance/tabs/ToolsTab";
import StockHistoryPakaiTab from "../components/maintenance/tabs/StockHistoryPakaiTab";
import StockHistoryTab from "../components/maintenance/tabs/StockHistoryTab";
import HistoryMesinTab from "../components/maintenance/tabs/HistoryMesinTab";
import HistoryToolsTab from "../components/maintenance/tabs/HistoryToolsTab";
import HistoryKaizenTab from "../components/maintenance/tabs/HistoryKaizenTab";
import StockPage from "../components/maintenance/tabs/StockPage";
import HistoryToolsDetail from "../components/maintenance/tabs/HistoryToolsDetail";
import StockToolsTab from "../components/maintenance/tabs/ToolsStockTab";
import StockToolsMinimumTab from "../components/maintenance/tabs/StockToolsMinimumPage";
import DetailLoan from "../components/maintenance/tabs/LoanPage";
import ChecksheetTab from "../components/user/tabs/ChecksheetTab";

import "./MaintenanceDashboard.css";

const MaintenanceDashboard = () => {
  const { currentUser, loading: authLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  const { tab, subtab, tool_id } = useParams();
  const [activeTab, setActiveTab] = useState("overview");
  const [mobileOpen, setMobileOpen] = useState(false);
  /* =========================
     UPDATE ACTIVE TAB BERDASARKAN URL
  ========================= */
  useEffect(() => {
    if (!tab) {
      setActiveTab("overview");
      return;
    }

    if (tab === "stock") {
      // ✅ JIKA TIDAK ADA SUBTAB → STOCK PAGE UTAMA
      if (!subtab) {
        setActiveTab("stock-page");
        return;
      }

      const stockTabs = {
        short: "stock-short",
        long: "stock-long",
        tools: "stock-tools",
        "tools-minimum": "stock-tools-minimum",
        "history-usage": "stock-history-usage",
        "history-stock": "stock-history-stock",
        "history-mesin": "stock-history-mesin",
        "history-tools": "stock-history-tools",
        "history-kaizen": "history-kaizen",
      };

      setActiveTab(stockTabs[subtab] || "stock-page");
      return;
    }

    // TAB BIASA
    const validTabs = [
      "overview",
      "machines",
      "tools",
      "active",
      "history",
      "sectors",
      "loan",
      "mttr",
      "kaizen",
      "checksheet",
    ];
    setActiveTab(validTabs.includes(tab) ? tab : "overview");
  }, [tab, subtab]);

  /* =========================
     AUTH GUARD
  ========================= */
  useEffect(() => {
    document.title = `  ${currentUser?.name || "Mesin Monitoring"}`;

    if (!authLoading && (!currentUser || currentUser.role !== "maintenance")) {
      navigate("/login");
    }
  }, [currentUser, authLoading, navigate]);

  /* =========================
     LOGOUT
  ========================= */
  const handleLogout = () => {
    localStorage.removeItem("token");
    window.location.href = "/login";
  };

  /* =========================
     RENDER TAB CONTENT
  ========================= */
  const renderTabContent = () => {
    if (authLoading) {
      return (
        <div className="d-flex justify-content-center align-items-center vh-100">
          <Spinner animation="border" />
        </div>
      );
    }

    // ✅ PRIORITAS DETAIL HISTORY TOOLS
    if (tab === "stock" && subtab === "history-tools" && tool_id) {
      return <HistoryToolsDetail />;
    }

    switch (activeTab) {
      case "overview":
        return <OverviewTab />;
      case "machines":
        return <MachinesTab />;
      case "tools":
        return <ToolsTab />;
      case "active":
        return <ActiveTab />;
      case "history":
        return <HistoryTab />;
      case "sectors":
        return <SectorsTab />;
      case "loan":
        return <DetailLoan />;
      case "kaizen":
        return <KaizenTab />;
      case "mttr":
        return <MTTRTab />;
      case "checksheet":
        return <ChecksheetTab />;
      case "stock-short":
        return <StockShortTab />;
      case "stock-long":
        return <StockLongTab />;
      case "stock-tools":
        return <StockToolsTab />;
      case "stock-history-usage":
        return <StockHistoryPakaiTab />;
      case "stock-history-stock":
        return <StockHistoryTab />;
      case "stock-history-mesin":
        return <HistoryMesinTab />;
      case "stock-history-tools":
        return <HistoryToolsTab />;
      case "history-kaizen":
        return <HistoryKaizenTab />;
      case "stock-page":
        return <StockPage />;
      case "stock-tools-minimum":
        return <StockToolsMinimumTab />;
      default:
        return <OverviewTab />;
    }
  };

  return (
    <MaintenanceProvider>
      <AlarmListener role="maintenance" />
      <div className="maintenance-dashboard-container">
        <Sidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          userRole="maintenance"
          onLogout={handleLogout}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
        />

        <div className="main-content">
          <TopNavbar
            activeTab={activeTab}
            currentUser={currentUser}
            handleLogout={handleLogout}
            userRole="maintenance"
          />

          <Container fluid className="px-3 py-2">
            {renderTabContent()}
          </Container>
        </div>
      </div>
    </MaintenanceProvider>
  );
};

export default MaintenanceDashboard;
